import React, { useState } from 'react';
import { OutputSettings as OutputSettingsPanel } from './OutputSettings';
import { LayerToggle } from './LayerToggle';
import type { OutputSettings, TemplateLayer } from '../../types';

type Tab = 'output' | 'layers';

interface PropertiesPanelProps {
  outputSettings: OutputSettings;
  onOutputChange: (settings: OutputSettings) => void;
  layers: TemplateLayer[];
  layerOverrides: Record<string, boolean>;
  onLayerToggle: (layerId: string, visible: boolean) => void;
}

export function PropertiesPanel({
  outputSettings,
  onOutputChange,
  layers,
  layerOverrides,
  onLayerToggle,
}: PropertiesPanelProps) {
  const [tab, setTab] = useState<Tab>('output');

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex border-b border-zinc-700 shrink-0">
        <TabBtn active={tab === 'output'} onClick={() => setTab('output')}>
          Output
        </TabBtn>
        <TabBtn active={tab === 'layers'} onClick={() => setTab('layers')}>
          Layers
          {Object.values(layerOverrides).some((v) => !v) && (
            <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" />
          )}
        </TabBtn>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'output' ? (
          <OutputSettingsPanel settings={outputSettings} onChange={onOutputChange} />
        ) : (
          <LayerToggle
            layers={layers}
            overrides={layerOverrides}
            onToggle={onLayerToggle}
          />
        )}
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1',
        active
          ? 'border-blue-500 text-white bg-zinc-800/40'
          : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/20',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
